import { Plus } from "lucide-react";
import { useId, useState } from "react";
import type { InvoiceSeries } from "@estelle/domain";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { HsnRates } from "@/modules/gst/screens/HsnRates";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/**
 * One row of the numbering table this screen holds in local state.
 *
 * Not `InvoiceSeries` itself: that type's `id` is a server-assigned primary
 * key and its `nextNumber` is a counter this screen must never originate a
 * value for (see the note above the numbering card). `key` is a client-only
 * identity for React's list rendering, thrown away the moment there is
 * somewhere real to save this.
 */
interface SeriesDraft {
  key: string;
  financialYear: string;
  prefix: string;
  kind: InvoiceSeries["kind"];
  isActive: boolean;
}

function blankSeries(kind: InvoiceSeries["kind"]): SeriesDraft {
  return {
    key: `local_${Math.random().toString(36).slice(2)}`,
    financialYear: "",
    prefix: "",
    kind,
    isActive: false,
  };
}

/**
 * The company's GST profile, tax-inclusive pricing, and invoice numbering
 * (FR-GST-1, FR-GST-11, ticket #94).
 *
 * `gst.settings.manage` is a separate permission from `settings.company.manage`
 * — see `config/modules.ts` — so this is deliberately a second screen rather
 * than a link to `settings/screens/CompanySettings.tsx`. A GST-specialist
 * role can hold one permission without the other. But the two screens would
 * otherwise show the same legal-identity fields from two places with no
 * repository backing either one, which is a worse failure mode than a
 * missing feature: two blank local drafts of "the same" data, neither
 * authoritative, is how a legal name ends up entered twice and differently.
 * So this screen does not re-render those fields — the identity card below
 * only points at where they live and explains the one field here that is
 * this module's own concern to get right. Once `M14-1` lands one repository
 * method backs both screens and they read the same record; until then,
 * duplicating the inputs would duplicate the drift, not the data.
 *
 * Local state only, like `CompanySettings.tsx`: there is no
 * `SettingsRepository` method for any of this yet, and this form does not
 * invent one. What the interface would need, when `M14-1` builds it:
 * `getCompanyGstProfile()` / `updateCompanyGstProfile(patch)` for the
 * profile and the pricing flag, and on `InvoicingRepository`, `listSeries()`
 * to read the real counters and a narrow `createSeries()` / `deactivateSeries()`
 * — never an `updateSeries()` that can touch `nextNumber`, for the reason in
 * the numbering card below.
 */
export function GstSettings() {
  const [pricesAreTaxInclusive, setPricesAreTaxInclusive] = useState(false);
  const [series, setSeries] = useState<SeriesDraft[]>([
    blankSeries("invoice"),
    blankSeries("credit_note"),
  ]);

  const inclusiveId = useId();

  function updateSeries(key: string, patch: Partial<SeriesDraft>) {
    setSeries((current) =>
      current.map((row) => (row.key === key ? { ...row, ...patch } : row)),
    );
  }

  return (
    <div className="max-w-3xl space-y-4">
      <HsnRates />

      <div className="rounded-md border border-dashed p-3 text-sm">
        <p className="font-medium">Nothing below saves yet.</p>
        <p className="text-muted-foreground">
          There is no company-settings or invoice-series repository method to
          write to — everything below lives in this component&apos;s state only
          and is gone on the next reload. It exists to show the shape FR-GST-1,
          FR-GST-11 and the invoice series ask for before there is somewhere to
          send it.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Company GST identity</CardTitle>
          <CardDescription>
            Legal name, trade name, GSTIN, address and invoice prefix are edited
            at Settings → Company, not here — see the comment at the top of this
            file for why this screen does not repeat that form.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <p>
            <span className="font-medium">Registered state</span> is the one
            field of that set worth calling out on this screen specifically:
            every invoice compares it against the order&apos;s own shipping
            state to decide the tax split (FR-GST-4) — same state means CGST +
            SGST, a different state means IGST. There is no third case for a
            domestic sale. Get this one field wrong at Settings → Company and
            every invoice issued afterward misfiles the same way, the same
            silent way, until a GST return does not reconcile.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Pricing</CardTitle>
          <CardDescription>
            Whether Shopify order prices already include tax (FR-GST-11).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-2">
            <Checkbox
              id={inclusiveId}
              checked={pricesAreTaxInclusive}
              onCheckedChange={(checked) => {
                setPricesAreTaxInclusive(checked === true);
              }}
            />
            <div className="grid gap-0.5">
              <Label htmlFor={inclusiveId}>Prices are tax-inclusive</Label>
              <p className="text-muted-foreground text-xs">
                Changes how a line&apos;s taxable value is derived from its
                price — unwound out of an inclusive price rather than taken
                as-is — and the invoice detail screen prints the derivation
                either way so a reader never has to take this flag&apos;s word
                for it.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Invoice numbering</CardTitle>
          <CardDescription>
            One gapless series per financial year, per document kind.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-muted-foreground text-xs">
            <span className="font-medium text-foreground">Next number</span> is
            a stored counter allocated inside the invoice&apos;s own
            transaction, not a value this screen can set — typing one in here
            would not reserve it, and if it ever did, two invoices issued the
            same second could still collide, or a rolled-back one could still
            leave a hole. A gap in a tax invoice series is a compliance finding,
            not a cosmetic defect, which is exactly why it stays disabled below
            rather than merely unlabelled.
          </p>

          <div className="space-y-3">
            {series.map((row) => (
              <div
                key={row.key}
                className="grid grid-cols-2 gap-3 rounded-md border p-3 sm:grid-cols-4"
              >
                <div className="space-y-1">
                  <Label htmlFor={`${row.key}-fy`}>Financial year</Label>
                  <Input
                    id={`${row.key}-fy`}
                    placeholder="2026-27"
                    value={row.financialYear}
                    onChange={(event) => {
                      updateSeries(row.key, {
                        financialYear: event.target.value,
                      });
                    }}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor={`${row.key}-prefix`}>Prefix</Label>
                  <Input
                    id={`${row.key}-prefix`}
                    placeholder="EL/26-27/"
                    value={row.prefix}
                    onChange={(event) => {
                      updateSeries(row.key, { prefix: event.target.value });
                    }}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor={`${row.key}-kind`}>Kind</Label>
                  <Select
                    value={row.kind}
                    onValueChange={(value) => {
                      // `onValueChange` is typed as `(value: string) => void`
                      // by the Select primitive, wider than `SeriesDraft.kind`
                      // — this guard is what narrows it back down instead of
                      // asserting it with `as`.
                      if (value === "invoice" || value === "credit_note") {
                        updateSeries(row.key, { kind: value });
                      }
                    }}
                  >
                    <SelectTrigger id={`${row.key}-kind`} className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="invoice">Invoice</SelectItem>
                      <SelectItem value="credit_note">Credit note</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label htmlFor={`${row.key}-next`}>Next number</Label>
                  <Input
                    id={`${row.key}-next`}
                    disabled
                    placeholder="Assigned by the server"
                  />
                </div>
                <div className="col-span-2 flex items-center gap-2 sm:col-span-4">
                  <Checkbox
                    id={`${row.key}-active`}
                    checked={row.isActive}
                    onCheckedChange={(checked) => {
                      updateSeries(row.key, { isActive: checked === true });
                    }}
                  />
                  <Label htmlFor={`${row.key}-active`}>
                    Active — new documents of this kind draw from this series
                  </Label>
                </div>
              </div>
            ))}
          </div>

          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => {
              setSeries((current) => [...current, blankSeries("invoice")]);
            }}
          >
            <Plus />
            Add a series
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
